/**
 * @file
 * CHECK24 The Bot Bridge - Communication bridge for The Bot AI chatbot.
 */

(function(window, document) {
    'use strict';

    var TheBotPresentationState = {
        FullPage: 'full-page',
        FullSheet: 'full-sheet',
        HalfSheet: 'half-sheet',
        PreviewChipsOnly: 'preview-chips-only',
        Minimized: 'minimized',
        Hidden: 'hidden'
    };

    var PageToBotEventKind = {
        ReadyToInitialize: 'ready-to-initialize',
        Initialize: 'initialize',
        RetrievePageContextResponse: 'retrieve-page-context-response',
        RetrievePageSectionResponse: 'retrieve-page-section-response',
        RetrieveAllowsPreviewChipsOnlyResponse: 'retrieve-allows-preview-chips-only-response',
        RetrievePageActionsResponse: 'retrieve-page-actions-response',
        SetPresentationState: 'set-presentation-state',
        GetPresentationState: 'get-presentation-state',
        SendMessage: 'send-message',
        ExecutePageActionAck: 'execute-page-action-ack',
        RetrieveInitialWorkflowsResponse: 'retrieve-initial-workflows-response',
    };

    var BotToPageEventKind = {
        ReadyToInitialize: 'ready-to-initialize',
        InitializeAck: 'initialize-ack',
        RetrievePageContext: 'retrieve-page-context',
        RetrievePageSection: 'retrieve-page-section',
        RetrieveAllowsPreviewChipsOnly: 'retrieve-allows-preview-chips-only',
        RetrievePageActions: 'retrieve-page-actions',
        RetrieveInitialWorkflows: 'retrieve-initial-workflows',
        SetPresentationStateAck: 'set-presentation-state-ack',
        GetPresentationStateResponse: 'get-presentation-state-response',
        SendMessageResponse: 'send-message-response',
        ExecutePageAction: 'execute-page-action'
    };

    // Version comparison helpers
    function normalizeVersion(version) {
        var parts = version.split('.').map(function(part) {
            return parseInt(part, 10);
        });
        if (parts.some(function(p) { return isNaN(p); })) {
            return undefined;
        }
        return parts;
    }

    function isVersionAtLeast(version, minimum) {
        var currentParts = normalizeVersion(version);
        var minimumParts = normalizeVersion(minimum);
        if (!currentParts || !minimumParts) {
            return false;
        }
        var length = Math.max(currentParts.length, minimumParts.length);
        for (var i = 0; i < length; i++) {
            var current = currentParts[i] || 0;
            var min = minimumParts[i] || 0;
            if (current > min) return true;
            if (current < min) return false;
        }
        return true;
    }

    function isTheBotWebComponentAvailable() {
        return document.querySelector('the-bot-embed') !== null;
    }

    function isInNativeApp() {
        // Check PHP-detected app flag first (reliable, based on URL parameter)
        if (window.c24TheBotSettings && window.c24TheBotSettings.isApp) return true;
        // Fallback: check native bridges
        if (typeof window.aiBotBridge !== 'undefined') return true;
        if (typeof window.webkit !== 'undefined' &&
            typeof window.webkit.messageHandlers !== 'undefined' &&
            typeof window.webkit.messageHandlers.aiBotBridge !== 'undefined') return true;
        return false;
    }

    function isTheBotAvailable(minAppVersion, appVersionFromSettings) {
        var inNativeApp = isInNativeApp();
        if (inNativeApp) {
            var appVersion = appVersionFromSettings;
            if (appVersion) {
                return isVersionAtLeast(appVersion, minAppVersion);
            }
            return true; // No version but in app - assume available
        }
        return true; // Web: bot is loaded on demand via theBotEnsureLoaded, no DOM element needed upfront
    }

    function isDeviceEnabled(enabledDevices) {
        // Default: all devices enabled
        if (!enabledDevices || enabledDevices.length === 0) {
            return true;
        }

        // Check if in native app
        if (isInNativeApp()) {
            return enabledDevices.indexOf('app') !== -1;
        }

        // Check device output (set globally by CMS)
        var deviceOutput = window.c24DeviceOutput || 'desktop';
        return enabledDevices.indexOf(deviceOutput) !== -1;
    }

    // Initialize C24TheBot immediately for native apps (required for bridge communication)
    function ensureC24TheBotExists() {
        if (!window.C24TheBot) {
            window.C24TheBot = {
                didReceiveEventFromAIBot: function(event) {
                    if (event.type === 'the-bot-bot-to-page' && window.C24TheBot.handleBotToPageEvent) {
                        window.C24TheBot.handleBotToPageEvent(event);
                    }
                },
                handleBotToPageEvent: null,
                isiOSBridgeAvailable: function() {
                    return window.webkit !== undefined &&
                        window.webkit.messageHandlers !== undefined &&
                        window.webkit.messageHandlers.aiBotBridge !== undefined;
                },
                isAndroidBridgeAvailable: function() {
                    return window.aiBotBridge !== undefined;
                }
            };
        }
    }

    // Bridge Implementation
    function TheBotBridgeImpl() {
        this.getPageContextCallback = null;
        this.getPageSectionCallback = null;
        this.allowsPreviewChipsOnlyCallback = null;
        this.getPageActionsCallback = null;
        this.getInitialWorkflowsCallback = null;
        this.pageActionCallbacks = {};
        this.botToPageEventSubscribers = [];
        this.initialized = false;
    }

    TheBotBridgeImpl.prototype.generateMessageId = function() {
        return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    };

    TheBotBridgeImpl.prototype.addBotToPageEventListener = function(listener) {
        var self = this;
        this.botToPageEventSubscribers.push(listener);
        return function() {
            var index = self.botToPageEventSubscribers.indexOf(listener);
            if (index > -1) {
                self.botToPageEventSubscribers.splice(index, 1);
            }
        };
    };

    TheBotBridgeImpl.prototype.emitBotToPageEvent = function(event) {
        for (var i = 0; i < this.botToPageEventSubscribers.length; i++) {
            try {
                this.botToPageEventSubscribers[i](event);
            } catch (e) {
                // Ignore subscriber errors
            }
        }
    };

    TheBotBridgeImpl.prototype.initializeBridge = function() {
        var self = this;
        return new Promise(function(resolve) {
            var eventHandler = function(event) {
                self.emitBotToPageEvent(event.detail);
            };

            ensureC24TheBotExists();

            window.C24TheBot.handleBotToPageEvent = eventHandler;
            window.addEventListener('the-bot-bot-to-page', eventHandler);

            var unsubscribeDefaultHandler = self.addBotToPageEventListener(function(e) {
                self.handleBotToPageEvent(e);
            });

            self.dispatchPageToBotEvent({ kind: PageToBotEventKind.ReadyToInitialize });

            self.waitForBotToPageEvent(BotToPageEventKind.ReadyToInitialize).then(function() {
                self.initialized = true;
                resolve(function cleanup() {
                    unsubscribeDefaultHandler();
                    window.C24TheBot.handleBotToPageEvent = null;
                    window.removeEventListener('the-bot-bot-to-page', eventHandler);
                });
            });
        });
    };

    TheBotBridgeImpl.prototype.handleBotToPageEvent = function(event) {
        var payload = event.payload;
        switch (payload.kind) {
        case BotToPageEventKind.RetrievePageContext:
            this.handleRetrievePageContext(event);
            break;
        case BotToPageEventKind.RetrievePageSection:
            this.handleRetrievePageSection(event);
            break;
        case BotToPageEventKind.RetrieveAllowsPreviewChipsOnly:
            this.handleRetrieveAllowsPreviewChipsOnly(event);
            break;
        case BotToPageEventKind.RetrievePageActions:
            this.handleRetrievePageActions(event);
            break;
        case BotToPageEventKind.RetrieveInitialWorkflows:
            this.handleRetrieveInitialWorkflows(event);
            break;
        case BotToPageEventKind.ExecutePageAction:
            this.handleExecutePageAction(event);
            break;
        }
    };

    TheBotBridgeImpl.prototype.handleRetrievePageContext = function(event) {
        var self = this;
        var sendResponse = function(result) {
            self.dispatchPageToBotEvent({
                kind: PageToBotEventKind.RetrievePageContextResponse,
                originalMessageId: event.messageId,
                result: result
            });
        };
        if (this.getPageContextCallback) {
            Promise.resolve(this.getPageContextCallback()).then(function(pageContext) {
                sendResponse({ kind: 'success', pageContext: pageContext });
            }).catch(function(error) {
                sendResponse({ kind: 'error', error: error.message || 'Unknown error' });
            });
        } else {
            sendResponse({ kind: 'success', pageContext: {} });
        }
    };

    TheBotBridgeImpl.prototype.handleRetrievePageSection = function(event) {
        var self = this;
        var sendResponse = function(result) {
            self.dispatchPageToBotEvent({
                kind: PageToBotEventKind.RetrievePageSectionResponse,
                originalMessageId: event.messageId,
                result: result
            });
        };
        if (this.getPageSectionCallback) {
            Promise.resolve(this.getPageSectionCallback()).then(function(section) {
                if (section) {
                    sendResponse({ kind: 'success', pageSection: section });
                } else {
                    sendResponse({ kind: 'error', error: 'No section callback registered' });
                }
            }).catch(function(error) {
                sendResponse({ kind: 'error', error: error.message || 'Unknown error' });
            });
        } else {
            sendResponse({ kind: 'error', error: 'No section callback registered' });
        }
    };

    TheBotBridgeImpl.prototype.handleRetrieveAllowsPreviewChipsOnly = function(event) {
        var self = this;
        var sendResponse = function(result) {
            self.dispatchPageToBotEvent({
                kind: PageToBotEventKind.RetrieveAllowsPreviewChipsOnlyResponse,
                originalMessageId: event.messageId,
                result: result
            });
        };
        if (this.allowsPreviewChipsOnlyCallback) {
            Promise.resolve(this.allowsPreviewChipsOnlyCallback()).then(function(allowed) {
                sendResponse({ kind: 'success', allowsPreviewChipsOnly: allowed });
            }).catch(function(error) {
                sendResponse({ kind: 'error', error: error.message || 'Unknown error' });
            });
        } else {
            sendResponse({ kind: 'success', allowsPreviewChipsOnly: true });
        }
    };

    TheBotBridgeImpl.prototype.handleRetrieveInitialWorkflows = function(event) {
        var self = this;
        var sendResponse = function(result) {
            self.dispatchPageToBotEvent({
                kind: PageToBotEventKind.RetrieveInitialWorkflowsResponse,
                originalMessageId: event.messageId,
                result: result
            });
        };
        if (this.getInitialWorkflowsCallback) {
            Promise.resolve(this.getInitialWorkflowsCallback()).then(function(initialWorkflows) {
                if (!initialWorkflows) {
                    sendResponse({ kind: 'error', error: 'No initial workflows callback registered' });
                    return;
                }
                sendResponse({ kind: 'success', initialWorkflows: initialWorkflows });
            }).catch(function(error) {
                sendResponse({ kind: 'error', error: error.message || 'Unknown error' });
            });
        } else {
            sendResponse({ kind: 'success', initialWorkflows: [] });
        }
    };

    TheBotBridgeImpl.prototype.handleRetrievePageActions = function(event) {
        var self = this;
        var sendResponse = function(result) {
            self.dispatchPageToBotEvent({
                kind: PageToBotEventKind.RetrievePageActionsResponse,
                originalMessageId: event.messageId,
                result: result
            });
        };
        if (this.getPageActionsCallback) {
            Promise.resolve(this.getPageActionsCallback()).then(function(pageActions) {
                for (var i = 0; i < pageActions.length; i++) {
                    var action = pageActions[i];
                    self.pageActionCallbacks[action.name] = action.execute;
                }
                var actionDefs = pageActions.map(function(action) {
                    return { name: action.name, description: action.description, schema: action.schema };
                });
                sendResponse({ kind: 'success', pageActions: actionDefs });
            }).catch(function(error) {
                sendResponse({ kind: 'error', error: error.message || 'Unknown error' });
            });
        } else {
            sendResponse({ kind: 'success', pageActions: [] });
        }
    };

    TheBotBridgeImpl.prototype.handleExecutePageAction = function(event) {
        var self = this;
        var callback = this.pageActionCallbacks[event.payload.name];
        var sendResponse = function(result) {
            self.dispatchPageToBotEvent({
                kind: PageToBotEventKind.ExecutePageActionAck,
                originalMessageId: event.messageId,
                result: result
            });
        };
        if (callback) {
            Promise.resolve(callback(event.payload.parameters)).then(function() {
                sendResponse({ kind: 'success' });
            }).catch(function(error) {
                sendResponse({ kind: 'error', error: 'Execution error: ' + (error.message || 'Unknown error') });
            });
        } else {
            sendResponse({ kind: 'error', error: 'Action not found' });
        }
    };

    TheBotBridgeImpl.prototype.dispatchPageToBotEvent = function(payload) {
        var messageId = this.generateMessageId();
        var eventData = { type: 'the-bot-page-to-bot', messageId: messageId, payload: payload };

        if (window.C24TheBot && window.C24TheBot.isiOSBridgeAvailable()) {
            window.webkit.messageHandlers.aiBotBridge.postMessage(eventData);
        } else if (window.C24TheBot && window.C24TheBot.isAndroidBridgeAvailable()) {
            window.aiBotBridge.sendEventToAIBot(JSON.stringify(eventData));
        } else {
            window.dispatchEvent(new CustomEvent('the-bot-page-to-bot', { detail: eventData }));
        }
        return messageId;
    };

    TheBotBridgeImpl.prototype.waitForBotToPageEvent = function(expectedKind, originalMessageId) {
        var self = this;
        return new Promise(function(resolve) {
            var unsubscribe = self.addBotToPageEventListener(function(event) {
                var payload = event.payload;
                if (payload.kind === expectedKind) {
                    if (originalMessageId && payload.originalMessageId) {
                        if (payload.originalMessageId === originalMessageId) {
                            unsubscribe();
                            resolve(payload);
                        }
                    } else {
                        unsubscribe();
                        resolve(payload);
                    }
                }
            });
        });
    };

    TheBotBridgeImpl.prototype.initializeTheBot = function(config) {
        this.getPageContextCallback = config.getPageContextCallback || null;
        this.getPageSectionCallback = config.getPageSectionCallback || null;
        this.allowsPreviewChipsOnlyCallback = config.allowsPreviewChipsOnlyCallback || null;
        this.getPageActionsCallback = config.getPageActionsCallback || null;
        this.getInitialWorkflowsCallback = config.getInitialWorkflowsCallback || null;

        var messageId = this.dispatchPageToBotEvent({
            kind: PageToBotEventKind.Initialize,
            agentSlug: config.agentSlug
        });
        return this.waitForBotToPageEvent(BotToPageEventKind.InitializeAck, messageId);
    };

    TheBotBridgeImpl.prototype.setPresentationState = function(presentationState) {
        var messageId = this.dispatchPageToBotEvent({
            kind: PageToBotEventKind.SetPresentationState,
            presentationState: presentationState
        });
        return this.waitForBotToPageEvent(BotToPageEventKind.SetPresentationStateAck, messageId);
    };

    TheBotBridgeImpl.prototype.getPresentationState = function() {
        var messageId = this.dispatchPageToBotEvent({ kind: PageToBotEventKind.GetPresentationState });
        return this.waitForBotToPageEvent(BotToPageEventKind.GetPresentationStateResponse, messageId).then(function(response) {
            return response.presentationState;
        });
    };

    TheBotBridgeImpl.prototype.sendMessage = function(message) {
        var botMessage;
        if (typeof message === 'string') {
            botMessage = { visible: true, parts: [{ kind: 'text', content: message }] };
        } else {
            botMessage = message;
        }
        var messageId = this.dispatchPageToBotEvent({ kind: PageToBotEventKind.SendMessage, message: botMessage });
        return this.waitForBotToPageEvent(BotToPageEventKind.SendMessageResponse, messageId);
    };

    var theBotBridge = new TheBotBridgeImpl();
    var _ensureTheBotInitialized = null;

    // Native app support handler (for bottom menu bar chat button)
    function getC24App() {
        return (typeof window.C24App !== 'undefined') ? window.C24App : null;
    }

    function setupNativeAppSupportHandler(openState) {
        var C24App = getC24App();
        if (C24App && typeof C24App.setSupportDialogHandler === 'function') {
            C24App.setSupportDialogHandler(function() {
                sendPresentationStateToNativeApp(openState);
                return true;
            });
            return true;
        }
        return false;
    }

    function sendPresentationStateToNativeApp(state) {
        var eventData = {
            type: 'the-bot-page-to-bot',
            messageId: 'wireframe-' + Date.now(),
            payload: { kind: PageToBotEventKind.SetPresentationState, presentationState: state }
        };
        if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.aiBotBridge) {
            window.webkit.messageHandlers.aiBotBridge.postMessage(eventData);
            return true;
        } else if (window.aiBotBridge) {
            window.aiBotBridge.sendEventToAIBot(JSON.stringify(eventData));
            return true;
        }
        return false;
    }

    function sendInitializeToNativeApp(agentSlug) {
        var eventData = {
            type: 'the-bot-page-to-bot',
            messageId: 'init-' + Date.now(),
            payload: { kind: PageToBotEventKind.Initialize, agentSlug: agentSlug }
        };
        if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.aiBotBridge) {
            window.webkit.messageHandlers.aiBotBridge.postMessage(eventData);
            return true;
        } else if (window.aiBotBridge) {
            window.aiBotBridge.sendEventToAIBot(JSON.stringify(eventData));
            return true;
        }
        return false;
    }

    // Wireframe contact button hook (web only)
    function setupWireframeHook() {
        var contactSelectors = ['.c24-contact', '.c24-contact-content', '.c24m-contact-icon-wrapper', '.c24m-contact-icon'];
        var inNativeApp = isInNativeApp();

        contactSelectors.forEach(function(selector) {
            var elements = document.querySelectorAll(selector);
            elements.forEach(function(element) {
                if (element.dataset.theBotHooked) return;
                element.dataset.theBotHooked = 'true';

                element.addEventListener('click', function(e) {
                    var openState = window.c24TheBotOpenState || TheBotPresentationState.FullSheet;
                    if (inNativeApp) {
                        e.preventDefault();
                        e.stopPropagation();
                        e.stopImmediatePropagation();
                        sendPresentationStateToNativeApp(openState);
                        return false;
                    }
                    if (_ensureTheBotInitialized) {
                        e.preventDefault();
                        e.stopPropagation();
                        e.stopImmediatePropagation();
                        _ensureTheBotInitialized().then(function() {
                            if (theBotBridge.initialized) {
                                theBotBridge.setPresentationState(openState);
                            }
                        });
                        return false;
                    }
                }, true);
            });
        });
    }

    // Check for showBot URL parameter
    function shouldAutoOpenBot() {
        var urlParams = new URLSearchParams(window.location.search);
        return urlParams.get('showBot') === '1';
    }

    // Initialization
    (function immediateInit() {
        var settings = window.c24TheBotSettings || {};
        var agentSlug = settings.agentSlug || '';
        var minAppVersion = settings.minAppVersion || '2025.37.0';
        var productKey = settings.productKey || '';
        var pageDescription = settings.pageDescription || 'User is viewing a ' + productKey + ' page on CHECK24';
        var initialState = settings.initialState || 'hidden';
        var openState = settings.openState || 'full-sheet';
        var appVersion = settings.appVersion || null;
        var enabledDevices = settings.enabledDevices || ['desktop', 'mobile', 'app'];
        var autoOpen = shouldAutoOpenBot();

        // Check if current device is enabled
        if (!isDeviceEnabled(enabledDevices)) {
            return; // Device not enabled - let default behavior (Chatty) work
        }

        if (!isTheBotAvailable(minAppVersion, appVersion)) {
            return; // Not available - let default behavior (Chatty) work
        }

        window.c24TheBotOpenState = openState;
        var inNativeApp = isInNativeApp();

        // For native apps: Initialize C24TheBot immediately and set up handler
        // The app manages The Bot itself, we just need to send presentation state changes
        if (inNativeApp) {
            ensureC24TheBotExists();
            // Send agent slug to native app so it uses the correct bot configuration
            sendInitializeToNativeApp(agentSlug);

            // Delay handler setup to override Chatty's handler (Chatty loads later)
            var setupHandler = function() {
                setupNativeAppSupportHandler(openState);
                setupWireframeHook();
                // Auto-open if showBot=1 parameter is present
                if (autoOpen) {
                    sendPresentationStateToNativeApp(openState);
                }
            };

            // Try multiple times to ensure we override Chatty's handler
            if (document.readyState === 'complete') {
                setTimeout(setupHandler, 500);
            } else {
                window.addEventListener('load', function() {
                    setTimeout(setupHandler, 500);
                });
            }

            return; // Don't wait for bridge initialization in native apps
        }

        // Web: On-demand initialization - only load and init the bot when needed
        var initialized = false;
        var initializingPromise = null;

        var botConfig = {
            agentSlug: agentSlug,
            getPageContextCallback: function() {
                return Promise.resolve((function() {
                    var ctx = {
                        description: pageDescription,
                        structured: {
                            product: productKey,
                            pageUrl: window.location.href,
                            pageTitle: document.title
                        }
                    };
                    try {
                        var custom = settings && settings.customContext;
                        if (custom && typeof custom === 'object') {
                            var reservedKeys = { product: true, pageUrl: true, pageTitle: true };
                            Object.keys(custom).forEach(function(key) {
                                if (!reservedKeys[key]) {
                                    var val = custom[key];
                                    ctx.structured[key] = (val === undefined || val === null) ? '' : String(val);
                                }
                            });
                        }
                    } catch (e) {
                        // noop: never break context creation
                    }
                    return ctx;
                })());
            },
            getPageSectionCallback: function() {
                return Promise.resolve({
                    key: productKey,
                    title: productKey.charAt(0).toUpperCase() + productKey.slice(1)
                });
            },
            allowsPreviewChipsOnlyCallback: function() {
                return Promise.resolve(!document.querySelector('.c24-sticky-cta, .sticky-cta, .js24Fixable, .mod24MobileMiniAc.fixed'));
            },
            getInitialWorkflowsCallback: function() {
                var workflows = settings.initialWorkflows;
                return Promise.resolve(Array.isArray(workflows) ? workflows : []);
            },
        };

        function ensureTheBotInitialized() {
            if (initialized) return Promise.resolve();
            if (initializingPromise) return initializingPromise;

            var ensureLoaded = typeof window.theBotEnsureLoaded === 'function'
                ? window.theBotEnsureLoaded()
                : Promise.resolve();

            initializingPromise = ensureLoaded.then(function() {
                return theBotBridge.initializeBridge();
            }).then(function() {
                return theBotBridge.initializeTheBot(botConfig);
            }).then(function() {
                initialized = true;
                initializingPromise = null;
                if (autoOpen) {
                    theBotBridge.setPresentationState(openState);
                } else if (initialState !== 'hidden') {
                    theBotBridge.setPresentationState(initialState);
                }
            }).catch(function(error) {
                initializingPromise = null;
                console.error('[c24TheBot] Initialization failed:', error);
            });

            return initializingPromise;
        }

        _ensureTheBotInitialized = ensureTheBotInitialized;

        function setupAndAutoInit() {
            var shouldAutoInit = autoOpen ||
                (typeof window.theBotShouldInitialize === 'function' && window.theBotShouldInitialize());
            if (shouldAutoInit) {
                ensureTheBotInitialized();
            }
            setTimeout(setupWireframeHook, 100);
        }

        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', setupAndAutoInit);
        } else {
            setupAndAutoInit();
        }
    })();
    // Public API
    window.c24TheBot = {
        bridge: theBotBridge,
        PresentationState: TheBotPresentationState,
        ensureTheBotInitialized: function() {
            return _ensureTheBotInitialized ? _ensureTheBotInitialized() : Promise.resolve();
        },
        open: function() { return theBotBridge.setPresentationState(TheBotPresentationState.FullSheet); },
        close: function() { return theBotBridge.setPresentationState(TheBotPresentationState.Hidden); },
        minimize: function() { return theBotBridge.setPresentationState(TheBotPresentationState.Minimized); },
        sendMessage: function(message) { return theBotBridge.sendMessage(message); }
    };

})(window, document);
